import React, { useState } from "react";
import { Range } from "react-range";
import { useTrain } from "./TrainContext";
import ResponsiveTooltip from "../../components/CustomTooltip/CustomTooltip";

interface SplitSliderProps {
  selectedIndexs: string | undefined;
  dataId: string;
  minTrain?: number;
  maxTrain?: number;
  minValidate?: number;
  maxValidate?: number;
  minTest?: number;
  maxTest?: number;
}

const RangeSelect: React.FC<SplitSliderProps> = ({
  selectedIndexs,
  dataId,
  minTrain = 70,
  maxTrain = 85,
  minValidate = 10,
  maxValidate = 20,
  minTest = 5,
  maxTest = 10,
}) => {
  const { dataIdDetails, setDataIdDetails } = useTrain();
  const [splitValues, setSplitValues] = useState({
    train: 0.8,
    validate: 0.1,
    test: 0.05,
  });

  const [split, setSplit] = useState([80, 90]);
  const trainValue = split[0];
  const validationValue = split[1] - split[0];
  const testValue = 100 - split[1];

  const handleSplitValuesChange = (newValues: number[]) => {
    const clampedTrain = Math.max(minTrain, Math.min(maxTrain, newValues[0]));
    const clampedValidate = Math.max(
      minValidate,
      Math.min(maxValidate, newValues[1] - clampedTrain)
    );

    // Adjust the remaining percentage for the test
    let remaining = 100 - clampedTrain - clampedValidate;
    const clampedTest = Math.max(minTest, Math.min(maxTest, remaining));

    // Ensure that train, validate, and test do not exceed 100%
    const finalValidate = Math.max(
      minValidate,
      Math.min(maxValidate, 100 - clampedTrain - clampedTest)
    );

    // Recalculate the test value after adjusting the validate
    const finalTest = 100 - clampedTrain - finalValidate;

    setSplit([clampedTrain, clampedTrain + finalValidate]);

    setSplitValues({
      train: clampedTrain / 100,
      validate: finalValidate / 100,
      test: finalTest / 100,
    });

    setDataIdDetails({
      ...dataIdDetails,
      advanceSetting: {
        ...dataIdDetails.advanceSetting,
        splitDataset: {
          ...dataIdDetails.advanceSetting.splitDataset,
          train: parseFloat((clampedTrain / 100).toFixed(2)),
          validate: parseFloat((finalValidate / 100).toFixed(2)),
          test: parseFloat((finalTest / 100).toFixed(2)),
        },
      },
    });
  };

  return (
    <ResponsiveTooltip title="Splits data into different groups for training, testing, and validation to ensure effective learning.">
      <div style={{ marginBottom: "2%", width: "50%" }}>
        <Range
          step={1}
          min={0}
          max={100}
          values={split}
          onChange={handleSplitValuesChange}
          renderTrack={({ props, children }) => (
            <div className="range-track" {...props}>
              <div
                className="range-track-train"
                style={{ width: `${trainValue}%` }}
              >
                <p className="range-label">
                  Train: <span>{trainValue}%</span>
                </p>
              </div>
              <div
                className="range-track-validate"
                style={{ width: `${validationValue}%`, left: `${trainValue}%` }}
              >
                <p className="range-label">
                  Valid: <span>{validationValue}%</span>
                </p>
              </div>
              <div
                className="range-track-test"
                style={{
                  width: `${testValue}%`,
                  left: `${trainValue + validationValue}%`,
                }}
              >
                <p className="range-label">
                  Test: <span>{testValue}%</span>
                </p>
              </div>
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div {...props} className="range-thumb" />
          )}
        />
      </div>
    </ResponsiveTooltip>
  );
};

export default RangeSelect;
